import { useState, useEffect } from "react";
import { useTheme } from "styled-components";

import { defaultToolkitTheme } from "@vinomofo/components";

type defaultOptions = {
  defaultIndex?: number;
};

export const useBreakpointIndex = (options: defaultOptions = {}): number => {
  const theme = useTheme() || defaultToolkitTheme;
  const { defaultIndex = 0 } = options;
  const breakpoints =
    (theme && theme.breakpoints) || defaultToolkitTheme.breakpoints;

  if (typeof defaultIndex !== "number") {
    throw new TypeError(
      `Default breakpoint index should be a number. Got: ${defaultIndex}, ${typeof defaultIndex}`
    );
  } else if (defaultIndex < 0 || defaultIndex > breakpoints.length - 1) {
    throw new RangeError(
      `Default breakpoint index out of range. Theme has ${breakpoints.length} breakpoints, got index ${defaultIndex}`
    );
  }

  const [value, setValue] = useState(defaultIndex);
  useEffect(() => {
    const getIndex = () =>
      breakpoints.filter(
        (breakpoint: string) =>
          window.matchMedia(`screen and (min-width: ${breakpoint})`).matches
      ).length - 1;

    const onResize = () => {
      const newValue = getIndex();
      if (value !== newValue) {
        setValue(newValue);
      }
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoints, value]);

  return value;
};

type Values<T> = T[] | T;

export const useResponsiveValue = <T,>(
  values: Values<T>,
  options: defaultOptions = {}
): T => {
  const theme = useTheme() || defaultToolkitTheme;
  const array = typeof values === "function" ? values(theme) : values;
  const final = Array.isArray(array) ? array : [array];
  const index = useBreakpointIndex(options);

  return final[index >= final.length ? final.length - 1 : index];
};
